import type { ObligationCatalogueRow } from './types';

export const CATALOGUE_VERSION = '2026-09-30.T1' as const;
export const CATALOGUE_EFFECTIVE_FROM = '2026-09-21' as const;
export const CATALOGUE_VERIFIED_ON = '2026-09-30' as const;
const act = 'Aged Care Act 2024 (Cth)';
const rules = 'Aged Care Rules 2025 (Cth)';

const row = (
  id: string,
  title: string,
  rule: ObligationCatalogueRow['rule'],
  provision: string,
  verification: ObligationCatalogueRow['verification'] = 'VERIFIED',
  headProviderDuty = true,
): ObligationCatalogueRow => ({
  id,
  title,
  rule,
  verification,
  headProviderDuty,
  tier: 'T1',
  version: CATALOGUE_VERSION,
  effectiveFrom: CATALOGUE_EFFECTIVE_FROM,
  verifiedOn: CATALOGUE_VERIFIED_ON,
  citation: {
    instrument:
      id === 'whs'
        ? 'Queensland WHS / Victorian OHS Acts'
        : id === 'privacy_notifiable_data_breaches'
          ? 'Privacy Act 1988 (Cth)'
          : provision.startsWith('s ')
            ? act
            : rules,
    provision,
  },
  scope: 'TENANT',
});

/**
 * Citations re-checked 22 Sep 2026 against Act Comp. 2 (C2026C00301), Rules
 * Comp. 10 (F2026C00802, ended 19 Sep 2026; Comp. 11 unpublished) and the
 * 20 Sep 2026 amending Rules F2026L01252 — see
 * reports/compliance-completion-2026-09-21/audit/14-legal-reverification-20260922.md.
 * Second independent primary-source read (30 Sep 2026):
 * docs/operations/clause-verifications/compliance-only-duties-2026-09-30.md.
 * Only the duties verified by that artifact are promoted here.
 *
 * Frozen legal catalogue. UNVERIFIED rows intentionally resolve to SHOW_ANYWAY;
 * they are catalogue entries, never an assertion that their condition is known.
 */
export const PROVIDER_OBLIGATION_CATALOGUE: readonly ObligationCatalogueRow[] = [
  row('quality_standards_1_4', 'Quality Standards 1–4', 'CATEGORIES_456', 'r 15-5(1), (1A)'),
  row(
    'quality_standard_5_1',
    'Outcome 5.1 clinical governance',
    'CATEGORY4_CARE_MANAGEMENT',
    'r 15-5(2), (2A), (3); r 15-30(1), (2)',
  ),
  row(
    'quality_standard_5_clinical_care',
    'Quality Standard 5 clinical care',
    'CATEGORIES_56',
    'r 15-5(2), (2A), (3); r 15-30(1), (2)',
  ),
  row(
    'quality_standard_6',
    'Quality Standard 6 food and nutrition',
    'CATEGORY6',
    'r 15-5(4); r 15-35',
  ),
  row(
    'quality_standard_7',
    'Quality Standard 7 residential community',
    'CATEGORY6',
    'r 15-5(4); r 15-40',
  ),
  row('incident_management', 'Incident management system', 'INCIDENT_MANAGEMENT', 's 164; r 164-1'),
  row('sirs', 'Serious Incident Response Scheme', 'UNIVERSAL', 'r 165A-10'),
  row(
    'complaints_feedback',
    'Complaints and feedback management system',
    'UNIVERSAL',
    's 165(1); rr 165-5 to 165-20',
    'VERIFIED',
  ),
  row('whistleblower', 'Whistleblower system and policy', 'UNIVERSAL', 'r 165-45 to 165-60'),
  row(
    'statement_of_rights',
    'Statement of Rights',
    'UNIVERSAL',
    's 23, s 24(2), s 144; r 15-10(5)(b)',
  ),
  row(
    'code_of_conduct_provider',
    'Code of Conduct — provider entity',
    'UNIVERSAL',
    'r 14-1(a), r 14-10(1)(a); s 145',
  ),
  row(
    'code_of_conduct_workers',
    'Code of Conduct — aged care workers',
    'UNIVERSAL',
    's 11(4)(b), s 173; r 14-1(b), r 14-10',
    'VERIFIED',
    false,
  ),
  row(
    'worker_screening',
    'Worker screening',
    'UNIVERSAL',
    'r 152-5; rr 152-15 to 152-17; rr 152-20 to 152-25',
    'VERIFIED',
    false,
  ),
  row('restrictive_practices', 'Restrictive practices', 'CATEGORY6', 'r 162-5; r 162-10'),
  row(
    'governing_body_composition',
    'Governing body independent non-executive composition',
    'GOVERNANCE_COMPOSITION',
    's 157(2)(a); r 157-5',
  ),
  row(
    'governing_body_clinical_member',
    'Governing body clinical experience member',
    'GOVERNANCE_CLINICAL_MEMBER',
    's 157(2)(b); r 157-10',
  ),
  row(
    'cooperative_governance_training',
    'Qualifying co-operative governance training',
    'COOP_TRAINING',
    's 157(5); r 157-15',
  ),
  row(
    'quality_care_advisory_body',
    'Quality care advisory body',
    'CATEGORIES_56',
    's 158(2); r 158-5',
  ),
  row(
    'consumer_advisory_body_offer',
    'Consumer advisory body offer',
    'CATEGORIES_56',
    's 158(4); r 158-20',
  ),
  row(
    'associated_provider_notification',
    'Associated-provider notification',
    'ASSOCIATED_PROVIDER_NOTIFICATION',
    'r 167-55',
  ),
  row('rn_24_7', '24/7 registered nurse', 'CATEGORY6', 's 175; r 175-5'),
  row('care_minutes', 'Care minutes', 'CATEGORY6', 's 176; r 176-15; r 176-20'),
  row(
    'quality_indicator_program',
    'Quality Indicator Program',
    'UNVERIFIED',
    'rr 166-105 to 166-185',
    'UNVERIFIED',
  ),
  row(
    'financial_prudential_standards',
    'Financial and Prudential Standards',
    'UNVERIFIED',
    'r 166-360; r 166-380',
    'UNVERIFIED',
  ),
  row(
    'acfr_qfr_reporting',
    'ACFR/QFR reporting',
    'UNVERIFIED',
    'r 166-310; r 166-340',
    'UNVERIFIED',
  ),
  row(
    'record_keeping',
    'Record keeping',
    'UNIVERSAL',
    's 154; rr 154-2, 154-150, 154-205, 154-910, 154-1000, 154-1205, 154-1300',
  ),
  row(
    'change_of_circumstances',
    'Change-of-circumstances notification',
    'UNIVERSAL',
    's 167; rr 167-10 to 167-60',
    'VERIFIED',
  ),
  row(
    'ipc_lead',
    'Infection prevention and control lead (operational)',
    'UNVERIFIED',
    'Lead appointment basis unverified; r 148-30(1), (3) prescribes a system, not a lead',
    'UNVERIFIED',
  ),
  row(
    'ipc_system',
    'Infection prevention and control system and PPE',
    'CATEGORIES_123',
    'r 148-30(1), (3)',
  ),
  row(
    'emergency_planning',
    'Emergency and disaster management planning',
    'UNVERIFIED',
    'rr 15-15, 15-25',
    'UNVERIFIED',
  ),
  row(
    'ndis_overlay',
    'NDIS incident reporting (basic manual)',
    'NDIS',
    'NDIS (Incident Management and Reportable Incidents) Rules 2018 ss 15, 20–21, 24; F1 5a–5e',
    'VERIFIED',
    false,
  ),
  row('dex', 'DSS Data Exchange (DEX)', 'CHSP', 'DSS DEX protocols', 'UNVERIFIED'),
  row(
    'whs',
    'Work health and safety',
    'JURISDICTIONAL',
    'Work Health and Safety Act 2011 (Qld) ss 35–39; Occupational Health and Safety Act 2004 (Vic) ss 38–39 (client-home control and Vic incident definition unverified)',
    'VERIFIED',
    false,
  ),
  row(
    'privacy_notifiable_data_breaches',
    'Privacy / Notifiable Data Breaches',
    'PRIVACY_APP_ENTITY',
    'Privacy Act 1988 (Cth) s 6D(4)(b); Part IIIC (health-service scope unverified)',
    'VERIFIED',
    false,
  ),
];
